/**
 * Multi-Study Flow Hook
 *
 * Main orchestration hook for the multi-study prescreening flow.
 * Manages state, coordinates matching logic, and tracks analytics.
 */

import { useReducer, useMemo, useCallback, useEffect } from "react";
import { MultiStudyMatcher } from "../matcher/MultiStudyMatcher";
import {
  flowReducer,
  initialFlowState,
  getCurrentStudy,
} from "../flow/flowReducer";
import { useMultiStudyAnalytics } from "./useMultiStudyAnalytics";
import { useQuestionnaireAnswersState } from "../../../components/contact-form/utils/questionnaireStorage";
import { getSavedZipcode, saveZipcode } from "../../../utils/zipcodeStorage";
import { Study } from "../types";
import { PreScreeningQuestion, QuestionType } from "../../../types/landingPage";
import { useKatchForEligibility } from "../../../hooks/useFeatureFlags";
import { useFetchTrialsByZipCodeAndConditions } from "../../../hooks/useFetchTrialsByZipCodeAndConditions";

interface UseMultiStudyFlowProps {
  landingPageId: string;
  commonQuestions: PreScreeningQuestion[];
  studies: Study[];
  onComplete: () => void;
}

/**
 * Hook for managing multi-study flow state and logic
 */
export function useMultiStudyFlow({
  landingPageId,
  commonQuestions,
  studies,
  onComplete,
}: UseMultiStudyFlowProps) {
  const [flowState, dispatch] = useReducer(flowReducer, initialFlowState);
  const [questionnaireAnswers, setQuestionnaireAnswers] =
    useQuestionnaireAnswersState();

  const useKatchToFetchEligibleTrials = useKatchForEligibility();
  const { fetchTrialIdsByZipCodeAndConditions, loading: isLoadingFetchTrialsByZipCodeAndConditions } = useFetchTrialsByZipCodeAndConditions();

  // Initialize matcher with studies
  const matcher = useMemo(() => new MultiStudyMatcher(studies), [studies]);

  // Analytics tracking
  const analytics = useMultiStudyAnalytics({ landingPageId });

  // Pre-fill zipcode from localStorage when entering zipcode stage
  useEffect(() => {
    if (flowState.stage === "zipcode") {
      const savedZipcode = getSavedZipcode();
      const currentZipcode = questionnaireAnswers.questionAnswers?.zipcode;

      // Only pre-fill if there's a saved zipcode and no current zipcode answer
      if (savedZipcode && !currentZipcode) {
        setQuestionnaireAnswers({
          ...questionnaireAnswers,
          questionAnswers: {
            ...questionnaireAnswers.questionAnswers,
            zipcode: savedZipcode,
          },
        });
      }
    }
  }, [flowState.stage]); // Intentionally minimal dependencies to run only on stage change

  /**
   * Handle completion of study-specific questions
   * MOVED UP before getCurrentQuestions to avoid forward reference
   */
  const handleStudySpecificComplete = useCallback((answers?: Record<string, string | string[]>) => {
    if (flowState.stage !== "study-specific") {
      console.error(
        `[useMultiStudyFlow] Cannot complete study-specific from stage: ${flowState.stage}`
      );
      return;
    }

    const currentStudy = getCurrentStudy(flowState);
    if (!currentStudy) {
      console.error("[useMultiStudyFlow] No current study");
      return;
    }

    // Use passed answers to avoid race condition with state updates
    const studyAnswers = answers || questionnaireAnswers.questionAnswers || {};

    // Extract the prescreening status from the passed answers to avoid race condition
    // The PreScreeningQuestionnaire now passes this as a special key
    const passedStatus = (answers as Record<string, unknown>)?.__prescreeningStatus;
    const passed = passedStatus === "passed";

    // Remove the internal status key from studyAnswers before saving
    const { __prescreeningStatus, ...cleanStudyAnswersRaw } = studyAnswers as Record<string, string | string[] | undefined>;
    // Filter out undefined values to match Answers type
    const cleanStudyAnswers = Object.fromEntries(
      Object.entries(cleanStudyAnswersRaw).filter(([_, v]) => v !== undefined)
    ) as Record<string, string | string[]>;

    const { commonAnswers, zipcode, eligibleStudies, currentStudyIndex } =
      flowState.data;

    if (passed) {
      // SUCCESS! Patient matched to this study
      analytics.trackStudyMatched(
        currentStudy,
        currentStudyIndex + 1,
        eligibleStudies.length
      );

      // Get trial ID (handle both string and object formats)
      const trialId =
        typeof currentStudy.trialId === "string"
          ? currentStudy.trialId
          : "defaultTrialId" in currentStudy.trialId
          ? currentStudy.trialId.defaultTrialId
          : undefined;

      // Save all answers with matched study info (use clean answers without internal status)
      setQuestionnaireAnswers({
        questionAnswers: {
          ...commonAnswers,
          zipcode,
          ...cleanStudyAnswers,
        },
        prescreeningStatus: "passed",
        matchedStudyId: currentStudy.id,
        matchedStudyTrialId: trialId,
      });

      dispatch({
        type: "COMPLETE_STUDY_SPECIFIC",
        payload: { passed: true, studyAnswers: cleanStudyAnswers },
      });

      onComplete();
      return;
    }

    // FAILED this study
    const remainingStudies = eligibleStudies.length - currentStudyIndex - 1;

    // Track that this study disqualified the user
    analytics.trackStudyDisqualified(
      currentStudy,
      currentStudyIndex,
      eligibleStudies.length
    );

    // Update failed studies list for debug panel
    const previousCascade = questionnaireAnswers.multiStudyCascade;
    const failedStudyIds = [
      ...(previousCascade?.failedStudyIds || []),
      currentStudy.id,
    ];

    // Check if there are more studies to try
    if (currentStudyIndex + 1 < eligibleStudies.length) {
      // Cascade to next study
      const nextStudy = eligibleStudies[currentStudyIndex + 1];
      const nextIndex = currentStudyIndex + 1;

      // Track the cascade to the next study
      analytics.trackStudyCascade(currentStudy, nextStudy, remainingStudies);

      analytics.trackStudyPrescreeningStarted(
        nextStudy,
        eligibleStudies.length,
        true
      );

      // Update cascade tracking for debug panel
      setQuestionnaireAnswers({
        ...questionnaireAnswers,
        multiStudyCascade: {
          eligibleStudyIds: eligibleStudies.map(s => s.id),
          currentStudyId: nextStudy.id,
          failedStudyIds,
          currentStudyIndex: nextIndex,
        },
      });

      dispatch({
        type: "COMPLETE_STUDY_SPECIFIC",
        payload: { passed: false, studyAnswers: cleanStudyAnswers },
      });

      // Questions will update automatically due to state change
      return;
    }

    // No more studies to check - disqualified from all
    analytics.trackAllStudiesFailed(eligibleStudies);

    setQuestionnaireAnswers({
      questionAnswers: {
        ...commonAnswers,
        zipcode,
        ...cleanStudyAnswers,
      },
      prescreeningStatus: "failed",
    });

    dispatch({
      type: "COMPLETE_STUDY_SPECIFIC",
      payload: { passed: false, studyAnswers: cleanStudyAnswers },
    });

    onComplete();
  }, [
    flowState,
    questionnaireAnswers,
    analytics,
    setQuestionnaireAnswers,
    onComplete,
  ]);

  /**
   * Get questions for current stage
   */
  const getCurrentQuestions = useCallback((): PreScreeningQuestion[] => {
    if (flowState.stage === "common") {
      return commonQuestions;
    }

    if (flowState.stage === "zipcode") {
      // Get saved zipcode for pre-filling
      const savedZipcode = getSavedZipcode();
      const currentZipcode = questionnaireAnswers.questionAnswers?.zipcode as string | undefined;

      return [
        {
          id: "zipcode",
          type: QuestionType.TEXT,
          question: "What is your zip code?",
          required: true,
          placeholder: "e.g., 90210",
          helpText: "We'll use this to match you with studies in your area.",
          defaultValue: currentZipcode || savedZipcode || undefined, // Pre-fill from storage or current answer
          validation: {
            pattern: /^\d{5}$/,
            message: "Please enter a valid 5-digit US zipcode",
          },
        },
      ];
    }

    if (flowState.stage === "study-specific") {
      const currentStudy = getCurrentStudy(flowState);
      if (!currentStudy) {
        console.error("[useMultiStudyFlow] No current study in study-specific stage");
        return [];
      }

      const questions = currentStudy.questions || [];

      // If study has no additional questions, auto-pass to that study
      // This happens when all criteria are in common questions
      if (questions.length === 0) {
        console.log(
          `[useMultiStudyFlow] Study ${currentStudy.id} has no additional questions - auto-passing`
        );
        // Auto-complete this study since there's nothing more to ask
        // We'll trigger this in the next render cycle to avoid state updates during render
        setTimeout(() => {
          handleStudySpecificComplete({
            __prescreeningStatus: "passed",
          } as Record<string, string | string[]>);
        }, 0);
        return [];
      }

      return questions;
    }

    return [];
  }, [flowState, commonQuestions, questionnaireAnswers.questionAnswers, handleStudySpecificComplete]);

  /**
   * Handle completion of common questions
   */
  const handleCommonComplete = useCallback((answers?: Record<string, string | string[]>) => {
    // Use passed answers to avoid race condition with state updates
    const commonAnswers = answers || questionnaireAnswers.questionAnswers || {};

    analytics.trackCommonQuestionsCompleted(Object.keys(commonAnswers).length);

    dispatch({
      type: "COMPLETE_COMMON",
      payload: { commonAnswers },
    });
  }, [questionnaireAnswers, analytics]);

  /**
   * Handle completion of zipcode
   */
  const handleZipcodeComplete = useCallback(async (answers?: Record<string, string | string[]>) => {
    // Use passed answers to avoid race condition with state updates
    const currentAnswers = answers || questionnaireAnswers.questionAnswers || {};
    const zipcode = (currentAnswers.zipcode as string) || "";

    // Save zipcode to localStorage for future sessions
    saveZipcode(zipcode);

    // Get common answers from flow state
    const commonAnswers = flowState.stage === "zipcode" ? flowState.data.commonAnswers : {};

    let locationEligibleStudies: Study[] = [];

    // If using Katch feature flag, fetch and use GraphQL data
    if (useKatchToFetchEligibleTrials) {
      try {
        const allAvailableTrialIds = await fetchTrialIdsByZipCodeAndConditions(zipcode, []);
        locationEligibleStudies = matcher.getStudies().filter(study => allAvailableTrialIds.includes(study.trialId as string));
      } catch (error) {
        console.error('Error fetching GraphQL trials:', error);
      }
    } else {
      locationEligibleStudies = matcher.filterByLocation(zipcode);
    }

    // Track location filtering (before age filtering)
    analytics.trackLocationFiltered(zipcode, locationEligibleStudies);

    // Filter by common answer requirements (e.g., age)
    const commonAnswerEligibleStudies = locationEligibleStudies.filter(study => {
      if (!study.commonAnswerRequirements) {
        return true; // No requirements, study is eligible
      }

      // Check each requirement
      for (const [questionId, allowedValues] of Object.entries(study.commonAnswerRequirements)) {
        const userAnswer = commonAnswers[questionId];

        if (!userAnswer) {
          // User hasn't answered this common question yet
          return false;
        }

        // Check if user's answer is in the allowed values
        if (!allowedValues.includes(userAnswer as string)) {
          return false;
        }
      }

      return true;
    });

    const sortedStudies = matcher.sortByPriority(commonAnswerEligibleStudies);

    if (sortedStudies.length === 0) {
      // No studies available in this location
      analytics.trackNoLocationMatch(zipcode);

      setQuestionnaireAnswers({
        questionAnswers: { ...questionnaireAnswers.questionAnswers, zipcode },
        prescreeningStatus: "failed",
      });

      dispatch({
        type: "NO_LOCATION_MATCH",
      });

      onComplete();
      return;
    }

    // Track that we're starting prescreening for the first study
    analytics.trackStudyPrescreeningStarted(
      sortedStudies[0],
      sortedStudies.length,
      false
    );

    // Save cascade tracking info for debug panel
    setQuestionnaireAnswers({
      questionAnswers: { ...questionnaireAnswers.questionAnswers, zipcode },
      multiStudyCascade: {
        eligibleStudyIds: sortedStudies.map(s => s.id),
        currentStudyId: sortedStudies[0].id,
        failedStudyIds: [],
        currentStudyIndex: 0,
      },
    });

    dispatch({
      type: "COMPLETE_ZIPCODE",
      payload: {
        zipcode,
        eligibleStudies: sortedStudies,
      },
    });
  }, [
    questionnaireAnswers,
    flowState,
    matcher,
    analytics,
    setQuestionnaireAnswers,
    onComplete,
    useKatchToFetchEligibleTrials,
    fetchTrialIdsByZipCodeAndConditions,
  ]);

  /**
   * Get handler for current stage
   */
  const getCurrentHandler = useCallback((): ((answers?: Record<string, string | string[]>) => void) => {
    switch (flowState.stage) {
      case "common":
        return handleCommonComplete;
      case "zipcode":
        return handleZipcodeComplete;
      case "study-specific":
        return handleStudySpecificComplete;
      default:
        // Wrap onComplete to match the handler signature
        return () => onComplete();
    }
  }, [
    flowState.stage,
    handleCommonComplete,
    handleZipcodeComplete,
    handleStudySpecificComplete,
    onComplete,
  ]);

  /**
   * Get title for current stage
   */
  const getCurrentTitle = useCallback(() => {
    switch (flowState.stage) {
      case "common":
        return "Eligibility Questions";
      case "zipcode":
        return "Your Location";
      case "study-specific":
        return "Additional Questions";
      default:
        return "";
    }
  }, [flowState.stage]);

  /**
   * Get subtitle for current stage
   */
  const getCurrentSubtitle = useCallback(() => {
    switch (flowState.stage) {
      case "common":
        return "Let's start with a few basic questions.";
      case "zipcode":
        return "We need your location to match you with the best available study.";
      case "study-specific":
        return "Just a few more questions to confirm your eligibility.";
      default:
        return "";
    }
  }, [flowState.stage]);

  return {
    flowState,
    currentQuestions: getCurrentQuestions(),
    currentHandler: getCurrentHandler(),
    currentTitle: getCurrentTitle(),
    currentSubtitle: getCurrentSubtitle(),
    currentStudy: getCurrentStudy(flowState),
    isLoading: isLoadingFetchTrialsByZipCodeAndConditions,
  };
}
